// @flow
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import { showAlert } from 'actions/app';
import { addFilter } from 'actions/dashboard';
import { addFilter as addFilterPhone } from 'actions/dashboardPhone';
import clsx from 'clsx';
import { getColumnsToRender } from 'hooks/datatable';
import { TableToolbar } from 'mui-datatables';
import { makeRequest } from 'services/utils';
import strings from 'strings';
import type { Filters, FiltersSection } from 'types/app';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import DataTable from 'UI/components/organisms/DataTable';
import { useDataTableStyles, useTableCard } from 'UI/globalStyles/DataTableStyles';
import { SvgDownloadThickBottom } from 'UI/res';
import { nestTernary } from 'UI/utils';

import { useStyles } from './styles';
import { formatRows } from './SummaryTable.utils';

const { actions: actionsCopies } = strings.dashboard.summaryTable;

type SummaryTableProps = {
  data?: Array,
  downloadFileName: string,
  emptyMessage: string,
  fetching?: boolean,
  lastUpdate?: string,
  onDownloadAll?: () => any,
  onRowClick: Filters => any,
  section: string,
  showDownloadAllButton?: boolean,
  showToolbar: boolean,
  url: string
};

const filtersConfig = {
  Recruiter: { name: 'recruiter', paramName: 'recruiterId' },
  Coach: { name: 'coach', paramName: 'coachId' }
};

const determineOnSearch = (section: FiltersSection, filter: Filters) =>
  section === 'dashboardPhone' ? addFilterPhone(filter) : addFilter(filter);

const SummaryTable = ({
  data,
  downloadFileName,
  emptyMessage,
  fetching,
  lastUpdate,
  onDownloadAll,
  onRowClick,
  section,
  showDownloadAllButton,
  showToolbar,
  url
}: SummaryTableProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const tableClasses = useTableCard();
  const dataTableClasses = useDataTableStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [type, setType] = useState('');

  useEffect(() => {
    typeof fetching === 'boolean' && setIsLoading(fetching);
  }, [fetching]);

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      const shouldMakeRequest = !data && url;
      const response =
        shouldMakeRequest &&
        (await makeRequest({
          url,
          method: 'get'
        }));

      if ((response.success && response.data) || data) {
        const finalData = response ? response.data : data;
        const mappedColumns = finalData?.columns.map(col => {
          const { align: alignDirection, ...restOptions } = col.options;
          const formatOptions = {
            align: {
              right: {
                setCellProps: () => ({ align: alignDirection }),
                setCellHeaderProps: () => ({ className: classes.numberHeader })
              }
            }
          };
          return {
            ...col,
            options: {
              ...restOptions,
              ...(formatOptions.align[alignDirection] || {})
            }
          };
        });
        const finalColumns = getColumnsToRender(mappedColumns);
        setColumns(finalColumns);
        setRows(formatRows(finalData.rows));
        if (finalData?.columns?.length > 0) {
          setType(finalData.columns[1].label);
        }
      } else {
        dispatch(showAlert(response.alert));
      }
      setIsLoading(false);
    })();
  }, [dispatch, url, data, lastUpdate, classes.numberHeader]);

  const handleRowClick = rowMeta => {
    const row = rows[rowMeta.rowIndex];
    const filterConfig = type && filtersConfig[type];
    if (filterConfig) {
      const filter = { [filterConfig.name]: { paramName: filterConfig.paramName, value: row } };
      dispatch(determineOnSearch(section, filter));
      onRowClick && onRowClick(filter);
    }
  };

  const handleDownloadAll = () =>
    onDownloadAll && onDownloadAll({ url, fileName: downloadFileName });

  const customTableToolbar = props => (
    <TableToolbar
      classes={{
        root: tableClasses.toolbar,
        actions: tableClasses.actions
      }}
      {...props}
    />
  );

  const className = clsx(showToolbar && dataTableClasses.withoutToolbar);

  const hasRows = rows?.length > 0;
  const defaultEmptyMessage = strings.dashboard.emptyMessage;

  return isLoading ? (
    <div className={classes.circularProgressContainer}>
      <CircularProgress />
    </div>
  ) : (
    nestTernary(
      hasRows,
      <DataTable
        className={className}
        columns={columns}
        data={rows}
        sort
        isServerSide={false}
        filter={false}
        search={false}
        download
        elevation={0}
        downloadOptions={{
          filename: `${downloadFileName}.csv`
        }}
        textLabels={{
          toolbar: {
            downloadCsv: actionsCopies.downloadCsv
          }
        }}
        components={{ TableToolbar: customTableToolbar }}
        CustomToolbar={
          showDownloadAllButton && (
            <div>
              <FPIconButton
                icon={SvgDownloadThickBottom}
                size="sm"
                className={classes.downloadAll}
                onClick={handleDownloadAll}
                tooltipProps={{ title: actionsCopies.downloadAll, arrow: false }}
              />
            </div>
          )
        }
        viewColumns
        pagination={false}
        selectableRowsHeader={false}
        selectableRows="none"
        onRowClick={handleRowClick}
      />,
      <div className={classes.emptyMessage}>{emptyMessage || defaultEmptyMessage}</div>
    )
  );
};

SummaryTable.defaultProps = {
  data: undefined,
  emptyMessage: undefined,
  fetching: undefined,
  lastUpdate: '',
  onDownloadAll: undefined,
  showDownloadAllButton: false,
  showToolbar: false
};

export default SummaryTable;
